"use client";

import LocaleLink from "@/component/layout/LocaleLink";
import { INVESTMENT, INVEST_CLOSING } from "@/data/investment-data";
import { useContent } from "@/hooks/useContent";
import type { Locale } from "@/types/home";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import type { InvestContent } from "./asset";
import InvestAssets from "./InvestAssets";
import InvestProcess from "./InvestProcess";

/** Investment page — thesis, returns strip, asset classes, process, CTA. */
export default function InvestmentParent() {
  const locale = useLocale() as Locale;
  const { data } = useContent<InvestContent>("investment");
  const title = data?.heading?.[locale] ?? INVESTMENT.title[locale];
  const lead = data?.intro?.[locale] ?? INVESTMENT.lead[locale];
  const returns = data?.stats ?? [];
  const closingTitle = data?.closing?.title?.[locale] ?? INVEST_CLOSING.title[locale];
  const closingCta = data?.closing?.cta?.[locale] ?? INVEST_CLOSING.cta[locale];

  return (
    <div className="bg-white text-ash dark:bg-ash dark:text-white">
      <section className="relative h-[70vh] min-h-[420px] w-full overflow-hidden bg-black">
        <motion.div initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0 bg-cover bg-center opacity-55" style={{ backgroundImage: `url('${INVESTMENT.image}')` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/40" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-360 px-6 pb-12 lg:px-10">
          <span className="text-[11px] uppercase tracking-widest text-white/70">{INVESTMENT.eyebrow[locale]}</span>
          <h1 className="mt-3 max-w-3xl font-bebas text-[clamp(2.6rem,7vw,6rem)] uppercase leading-[0.95] text-white">{title}</h1>
          <p className="mt-4 max-w-xl text-white/70">{lead}</p>
        </div>
      </section>

      {returns.length > 0 && (
        <section className="border-b border-black/10 dark:border-white/10">
          <div className="mx-auto grid max-w-360 grid-cols-2 md:grid-cols-4">
            {returns.map((r, i) => (
              <div key={r.id} className={`px-6 py-12 lg:px-10 ${i > 0 ? "border-s border-black/10 dark:border-white/10" : ""}`}>
                <div className="font-bebas text-[clamp(2.5rem,5vw,4rem)] leading-none text-accent">{r.value[locale]}</div>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{r.label[locale]}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <InvestAssets />
      <InvestProcess />

      <section className="bg-accent px-6 py-24 text-center text-white md:py-32">
        <h2 className="font-bebas text-[clamp(2.5rem,6vw,5rem)] uppercase leading-none">{closingTitle}</h2>
        <LocaleLink href="/contact" className="mt-8 inline-flex items-center bg-white px-10 py-4 text-[12px] font-semibold uppercase tracking-widest text-ash transition-transform hover:-translate-y-0.5">
          {closingCta}
        </LocaleLink>
      </section>
    </div>
  );
}
